import { css, cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';
import { DotProps } from './types';

type DotStylesProps = Omit<DotProps, 'innerRef'>;

const DOT_SIZE = 16;

const _colorMap = {
  warning: theme.colors.status.warning,
  success: theme.colors.status.success,
  critical: theme.colors.status.critical,
  primary: theme.colors.primary50,
  neutral: theme.colors.neutral20,
};

export type DotThemeValues = typeof _colorMap;

const dotBaseStyle = css`
  border-radius: 50%;
  position: absolute;
`;

// Each color sets the dot background and the pulsing ::after background. When the
// dot is not pulsing the ::after has no `content`, so it is never rendered.
const dotColorStyles = {
  warning: css`
    background: ${theme.colors.status.warning};
    &::after {
      background: ${theme.colors.status.warning};
    }
  `,
  success: css`
    background: ${theme.colors.status.success};
    &::after {
      background: ${theme.colors.status.success};
    }
  `,
  critical: css`
    background: ${theme.colors.status.critical};
    &::after {
      background: ${theme.colors.status.critical};
    }
  `,
  primary: css`
    background: ${theme.colors.primary50};
    &::after {
      background: ${theme.colors.primary50};
    }
  `,
  neutral: css`
    background: ${theme.colors.neutral20};
    &::after {
      background: ${theme.colors.neutral20};
    }
  `,
};

const dotStaticStyle = css`
  width: ${DOT_SIZE}px;
  height: ${DOT_SIZE}px;
  border: 3px solid ${theme.colors.white};
`;

const dotPulsingStyle = css`
  --dot-size: 8px;
  width: var(--dot-size);
  height: var(--dot-size);

  &::after {
    border-radius: 50%;
    content: '';
    display: block;
    height: var(--dot-size);
    left: 0px;
    position: absolute;
    top: 0px;
    width: var(--dot-size);
    animation: dot-pulse 2s ease 0s infinite;
  }

  @keyframes dot-pulse {
    0% {
      opacity: 0.75;
      transform: scale(1);
    }
    100% {
      opacity: 0;
      transform: scale(2);
    }
  }
`;

export const getDotClassName = ({ color, pulsing }: DotStylesProps) =>
  cx(dotBaseStyle, dotColorStyles[color], pulsing ? dotPulsingStyle : dotStaticStyle);
